// ChatContext.js
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  collection,
  collectionGroup,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  setDoc,
  getDoc,
  getDocs,
  doc,
  documentId,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import { db } from "../config/firebase";

const ChatContext = createContext();

export const useChat = () => useContext(ChatContext);

export const ChatProvider = ({ currentUserId, children }) => {
  const [chatUsers, setChatUsers] = useState([]);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [lastMessages, setLastMessages] = useState({});
  const [chatMessages, setChatMessages] = useState([]);
  const [globalSearch, setGlobalSearch] = useState("");

  const messagesUnsubRef = useRef(null);
  const currentUser = JSON.parse(localStorage.getItem("userProfile"));

  const tsToDate = (ts) => (ts && ts.toDate ? ts.toDate() : ts || null);

  const getChatId = (u1, u2) => (u1 <= u2 ? `${u1}_${u2}` : `${u2}_${u1}`);

  // 👂 Listen to all chats

  useEffect(() => {
    if (!currentUserId) return;
    const q = collectionGroup(db, "messages");

    const unsub = onSnapshot(q, async (snapshot) => {
      const userIdsSet = new Set();
      const unreadMap = {};
      const lastMsgMap = {};

      // 1️⃣ FIRST: BUILD unreadMap completely
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        const senderId = data.senderId;
        const receiverId = data.receiverId;
        // const isRead = data.isRead ?? true;
        const isRead = data.isRead ?? false;
        const timestamp = tsToDate(data.timestamp);
        const text = data.text ?? data.message ?? "";

        if (!senderId || !receiverId) return;

        // build user list
        if (senderId === currentUserId) userIdsSet.add(receiverId);
        if (receiverId === currentUserId) userIdsSet.add(senderId);

        // unread logic
        if (receiverId === currentUserId && isRead === false) {
          unreadMap[senderId] = (unreadMap[senderId] || 0) + 1;
        }

        // last message logic
        const chatId = getChatId(senderId, receiverId);
        const existing = lastMsgMap[chatId];
        if (!existing || existing.timestamp < timestamp) {
          lastMsgMap[chatId] = {
            id: docSnap.id,
            text,
            isRead,
            senderId,
            receiverId,
            timestamp,
          };
        }
      });

      // 2️⃣ AFTER snapshot loop is finished
      const userIds = Array.from(userIdsSet);
      if (userIds.length === 0) {
        setChatUsers([]);
        setUnreadCounts({});
        return;
      }

      const allUsers = [];
      const chunkSize = 10;

      // 3️⃣ Fetch all user profiles
      for (let i = 0; i < userIds.length; i += chunkSize) {
        const chunk = userIds.slice(i, i + chunkSize);
        const qUsers = query(
          collection(db, "users"),
          where(documentId(), "in", chunk)
        );
        const snap = await getDocs(qUsers);

        snap.forEach((d) => {
          const data = d.data();
          const unreadCount = unreadMap[d.id] || 0;
          const chatId = getChatId(currentUserId, d.id);
          const lastMsg = lastMsgMap[chatId] || null;

          allUsers.push({
            id: d.id,
            name: data.name || "Unknown",
            profileImage: data.profileImage || "",
            isOnline: data.isOnline || false,
            lastActive: tsToDate(data.lastActive),
            unreadCount, // ✔ Always correct now
            lastMessage: lastMsg ? lastMsg.text : "",
            lastMessageTime: lastMsg ? lastMsg.timestamp : null,
            lastMessageSender: lastMsg ? lastMsg.senderId : null,
            lastMessageType: lastMsg ? lastMsg.type || "text" : "text",
          });
        });
      }

      // 4️⃣ Sort by last message
      allUsers.sort((a, b) => {
        const aMsg = lastMsgMap[getChatId(currentUserId, a.id)]?.timestamp;
        const bMsg = lastMsgMap[getChatId(currentUserId, b.id)]?.timestamp;
        if (!aMsg && !bMsg) return 0;
        if (!aMsg) return 1;
        if (!bMsg) return -1;
        return bMsg - aMsg;
      });

      setChatUsers(allUsers);
      setUnreadCounts(unreadMap);
    });

    return () => unsub();
  }, [currentUserId]);

  // 🔥 Listen to a single chat
  const listenToMessages = (receiverId) => {
    if (!currentUserId) return;
    const chatId = getChatId(currentUserId, receiverId);
    if (messagesUnsubRef.current) messagesUnsubRef.current();

    const q = query(
      collection(db, "chats", chatId, "messages"),
      orderBy("timestamp", "asc")
    );
    messagesUnsubRef.current = onSnapshot(q, (snapshot) => {
      const msgs = snapshot.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          senderId: data.senderId,
          receiverId: data.receiverId,
          text: data.text ?? data.message ?? "",
          timestamp: tsToDate(data.timestamp),
          isRead: data.isRead || false,
          type: data.type || "text",
          replyTo: data.replyTo || null,
        };
      });
      setChatMessages(msgs);
    });
  };

  const stopListeningToMessages = () => {
    if (messagesUnsubRef.current) {
      messagesUnsubRef.current();
      messagesUnsubRef.current = null;
      setChatMessages([]);
    }
  };

  // 📨 Send a message
  const sendMessage = async (receiverId, text, replyTo = null) => {
    if (!currentUserId) return;
    const chatId = getChatId(currentUserId, receiverId);

    const receiverRef = doc(db, "chats", chatId, "participants", receiverId);
    const receiverSnap = await getDoc(receiverRef);
    const receiverOnline =
      receiverSnap.exists() && receiverSnap.data().isOnline;

    const messagesRef = collection(db, "chats", chatId, "messages");
    await addDoc(messagesRef, {
      senderId: currentUserId,
      receiverId,
      text,
      isRead: receiverOnline ? true : false,
      replyTo,
      type: "text",
      timestamp: serverTimestamp(),
    });
  };

  // 📀 Send tokens
  const sendChatTokens = async (receiverId, amount) => {
    if (!currentUserId) return;
    const chatId = getChatId(currentUserId, receiverId);
    const receiverRef = doc(db, "chats", chatId, "participants", receiverId);
    const receiverSnap = await getDoc(receiverRef);
    const receiverOnline =
      receiverSnap.exists() && receiverSnap.data().isOnline;
    const messagesRef = collection(db, "chats", chatId, "messages");
    await addDoc(messagesRef, {
      senderId: currentUserId,
      receiverId,
      text: `Sent ${amount} Token`,
      isRead: receiverOnline ? true : false,
      type: "token",
      timestamp: serverTimestamp(),
    });
  };

  // ✅ Mark messages as read
  const markUserMessagesAsRead = async (partnerId) => {
    if (!currentUserId) return;
    const chatId = getChatId(currentUserId, partnerId);
    const messagesRef = collection(db, "chats", chatId, "messages");
    const q = query(
      messagesRef,
      where("receiverId", "==", currentUserId),
      where("isRead", "==", false)
    );
    const snap = await getDocs(q);

    const batch = writeBatch(db);
    snap.forEach((d) => batch.update(d.ref, { isRead: true }));
    await batch.commit();

    setUnreadCounts((prev) => {
      const copy = { ...prev };
      delete copy[partnerId];
      return copy;
    });
  };

  // 🟢 Update online/offline
  const setGlobalOnlineStatus = async (isOnline) => {
    if (!currentUserId) return;

    const userRef = doc(db, "users", currentUserId);

    await setDoc(
      userRef,
      {
        isOnline,
        lastActive: serverTimestamp(),
      },
      { merge: true }
    );
  };

  // 🧩 Create chat link if not exists
  const createChatIfNotExists = async (otherUser) => {
    if (!currentUserId) return;
    const myRef = doc(db, "users", currentUserId, "users", otherUser.id);
    const otherRef = doc(db, "users", otherUser.id, "users", currentUserId);

    const snap = await getDoc(myRef);
    if (!snap.exists()) {
      await setDoc(myRef, {
        _id: otherUser.id,
        name: otherUser.name.toLowerCase(),
        profileImage: otherUser.profileImage || "",
        createdAt: serverTimestamp(),
      });

      await setDoc(otherRef, {
        _id: currentUserId,
        name: "You",
        createdAt: serverTimestamp(),
      });
    }
  };

  //   Get All users
  const getAllUsersFromFirestore = async (currentUserId) => {
    try {
      const snap = await getDocs(collection(db, "users"));
      const users = snap.docs
        .map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
        // optionally filter out yourself:
        .filter((u) => u.id !== currentUserId);

      return users;
    } catch (error) {
      console.error("🔥 Error fetching all users:", error);
      return [];
    }
  };

  // 🔹 Listen to group messages
  const listenToTournamentChat = (tournamentId, setLoading) => {
    if (!tournamentId) return;

    setLoading(true);

    const q = query(
      collection(db, "tournament_chats", tournamentId, "messages"),
      orderBy("timestamp", "asc")
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const msgs = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
          timestamp: d.data().timestamp?.toDate?.() || null,
        }));
        setChatMessages(msgs);
        setLoading(false);
      },
      (error) => {
        console.error("🔥 Error listening to tournament chat:", error);
        setLoading(false);
      }
    );

    return unsubscribe; // remember to cleanup in component
  };

  // 🔹 Send group message
  const sendTournamentMessage = async (tournamentId, message) => {
    if (!message.trim() || !currentUser?._id) return;
    try {
      await addDoc(
        collection(db, "tournament_chats", tournamentId, "messages"),
        {
          senderId: currentUser._id,
          senderName: currentUser.name,
          senderImage: currentUser.profileImage || "",
          message: message.trim(),
          type: "text",
          timestamp: serverTimestamp(),
        }
      );
    } catch (error) {
      console.error("🔥 Error sending tournament message:", error);
    }
  };

  // 🔹 Listen to global thread
  const listenToGlobalThread = async (setMessages) => {
    const q = query(
      collection(db, "global_thread"),
      orderBy("timestamp", "asc")
    );

    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const msgs = snap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            senderId: data.senderId,
            senderName: data.senderName,
            senderImage: data.senderImage,
            text: data.text ?? data.message ?? "",
            timestamp: data.timestamp?.toDate
              ? data.timestamp.toDate()
              : data.timestamp
              ? new Date(data.timestamp)
              : null,
            raw: data,
          };
        });

        setChatMessages(msgs);
      },
      (err) => {
        console.error("Global thread listen error:", err);
      }
    );

    return unsubscribe;
  };

  // 🔹 Send Global thread message
  const sendGlobalMessage = async (text) => {
    if (!currentUser || !currentUser._id || !text || !text.trim()) return;
    try {
      await addDoc(collection(db, "global_thread"), {
        senderId: currentUser._id,
        senderName: currentUser.name || "Unknown",
        senderImage: currentUser.profileImage || "",
        text: text.trim(),
        timestamp: serverTimestamp(),
      });
    } catch (err) {
      console.error("Error sending global message:", err);
      throw err;
    }
  };

  // Ensure Participants
  const ensureParticipants = async (receiverId) => {
    if (!currentUserId) return;

    const chatId = getChatId(currentUserId, receiverId);

    const myRef = doc(db, "chats", chatId, "participants", currentUserId);
    const otherRef = doc(db, "chats", chatId, "participants", receiverId);

    const mySnap = await getDoc(myRef);
    const otherSnap = await getDoc(otherRef);

    const data = {
      isOnline: true,
      lastActive: serverTimestamp(),
    };

    if (!mySnap.exists()) {
      await setDoc(myRef, data);
    } else {
      await setDoc(myRef, { isOnline: true }, { merge: true });
    }

    if (!otherSnap.exists()) {
      await setDoc(otherRef, {
        isOnline: false, // other user is not online in *my app*
        lastActive: serverTimestamp(),
      });
    }
  };

  // Search users by name or handleName
  const searchUsers = async (searchText) => {
    try {
      if (!searchText.trim()) return [];
      // searchText = searchText.toLowerCase();
      const usersRef = collection(db, "users");
      const text = searchText.toLowerCase();

      // Firestore query for name startsWith search
      const q1 = query(
        usersRef,
        where("name", ">=", text),
        where("name", "<=", text + "\uf8ff")
      );

      // Firestore query for handleName startsWith search
      const q2 = query(
        usersRef,
        where("handleName", ">=", text),
        where("handleName", "<=", text + "\uf8ff")
      );

      // Run both queries
      const [snap1, snap2] = await Promise.all([getDocs(q1), getDocs(q2)]);

      let results = [];

      snap1.forEach((doc) => results.push({ id: doc.id, ...doc.data() }));
      snap2.forEach((doc) => results.push({ id: doc.id, ...doc.data() }));

      // remove duplicates
      const uniqueResults = Array.from(
        new Map(results.map((u) => [u.id, u])).values()
      );

      return uniqueResults;
    } catch (error) {
      console.error("🔥 Error searching users:", error);
      return [];
    }
  };

  // const convertAllUserNamesToLowercase = async () => {
  //   try {
  //     console.log("run lower case");
  //     const snap = await getDocs(collection(db, "users"));

  //     const batch = writeBatch(db);

  //     snap.forEach((d) => {
  //       const data = d.data();

  //       const updated = {};

  //       if (data.name) {
  //         updated.nameLower = data.name.toLowerCase();
  //       }

  //       if (data.handleName) {
  //         updated.handleNameLower = data.handleName.toLowerCase();
  //       }

  //       batch.update(doc(db, "users", d.id), updated);
  //     });

  //     await batch.commit();
  //     console.log("🔥 All user names converted to lowercase successfully!");
  //   } catch (err) {
  //     console.error("❌ Error converting user names:", err);
  //   }
  // };

  const value = {
    globalSearch,
    setGlobalSearch,
    chatUsers,
    unreadCounts,
    lastMessages,
    chatMessages,
    listenToMessages,
    stopListeningToMessages,
    sendMessage,
    sendChatTokens,
    markUserMessagesAsRead,
    createChatIfNotExists,
    getChatId,
    getAllUsersFromFirestore,
    listenToTournamentChat,
    sendTournamentMessage,
    listenToGlobalThread,
    sendGlobalMessage,
    ensureParticipants,
    setGlobalOnlineStatus,
    searchUsers,
    // convertAllUserNamesToLowercase,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};
